import type { ItemIndex } from '../../doc/ItemIndex';
import { rotatedBounds, type Rect } from '../../doc/geometry';
import type { Geometry } from '../../doc/schema';
import type { Handle } from './transforms';

/**
 * A line another item offers as a snap target, in world units: its position `at` on one axis
 * and the item's extent `lo..hi` along the other, which says how far away the item is.
 */
export interface SnapLine {
  at: number;
  lo: number;
  hi: number;
}

/** Vertical (x) and horizontal (y) lines other items offer as snap targets. */
export interface SnapCandidates {
  xs: SnapLine[];
  ys: SnapLine[];
}

/**
 * A guide to draw for a snap: the line at `at` (an x for a vertical guide, a y for a horizontal
 * one), running `from`..`to` along the other axis so it joins the moved item and the one it met.
 */
export interface SnapGuide {
  at: number;
  from: number;
  to: number;
}

export interface SnapResult {
  dx: number;
  dy: number;
  /** Matched guides, or null when no snap happened on that axis. */
  gx: SnapGuide | null;
  gy: SnapGuide | null;
}

/** Edges and centres of every item intersecting `area`, except the excluded ones and ink strokes. */
export function collectCandidates(index: ItemIndex, area: Rect, exclude: ReadonlySet<string>): SnapCandidates {
  const xs: SnapLine[] = [];
  const ys: SnapLine[] = [];
  for (const item of index.query(area)) {
    if (exclude.has(item.id) || item.type === 'stroke' || item.type === 'connector') continue;
    const b = rotatedBounds(item);
    for (const at of [b.x, b.x + b.w / 2, b.x + b.w]) xs.push({ at, lo: b.y, hi: b.y + b.h });
    for (const at of [b.y, b.y + b.h / 2, b.y + b.h]) ys.push({ at, lo: b.x, hi: b.x + b.w });
  }
  return { xs, ys };
}

/**
 * The closest line to any of `edges` within `threshold`, among the lines whose item lies within
 * `reach` of the moving span `s0..s1` (the gap between them along the line). On a tie the
 * nearer item wins, so the guide points at the neighbour rather than at something far off.
 */
function best(edges: number[], lines: SnapLine[], threshold: number, s0: number, s1: number, reach: number): { delta: number; line: SnapLine } | null {
  let out: { delta: number; line: SnapLine } | null = null;
  let outGap = Infinity;
  for (const line of lines) {
    const gap = Math.max(0, line.lo - s1, s0 - line.hi);
    if (gap > reach) continue;
    for (const e of edges) {
      const d = line.at - e;
      const ad = Math.abs(d);
      if (ad > threshold) continue;
      const cur = out ? Math.abs(out.delta) : Infinity;
      if (ad < cur - 1e-9 || (ad <= cur + 1e-9 && gap < outGap)) {
        out = { delta: d, line };
        outGap = gap;
      }
    }
  }
  return out;
}

/** The guide for a matched line, spanning it and the moved item's final extent `s0..s1`. */
const guide = (line: SnapLine, s0: number, s1: number): SnapGuide => ({ at: line.at, from: Math.min(line.lo, s0), to: Math.max(line.hi, s1) });

/**
 * Snap a moving rect (its left/centre/right and top/centre/bottom) to candidate lines of items
 * within `reach` and, optionally, its top-left to a grid. Returns the correction to add.
 */
export function snapRect(rect: Rect, cands: SnapCandidates, threshold: number, grid: number | null, reach = Infinity): SnapResult {
  const res: SnapResult = { dx: 0, dy: 0, gx: null, gy: null };
  const bx = best([rect.x, rect.x + rect.w / 2, rect.x + rect.w], cands.xs, threshold, rect.y, rect.y + rect.h, reach);
  const by = best([rect.y, rect.y + rect.h / 2, rect.y + rect.h], cands.ys, threshold, rect.x, rect.x + rect.w, reach);
  if (bx) res.dx = bx.delta;
  else if (grid) {
    const g = Math.round(rect.x / grid) * grid - rect.x;
    if (Math.abs(g) <= threshold) res.dx = g;
  }
  if (by) res.dy = by.delta;
  else if (grid) {
    const g = Math.round(rect.y / grid) * grid - rect.y;
    if (Math.abs(g) <= threshold) res.dy = g;
  }
  if (bx) res.gx = guide(bx.line, rect.y + res.dy, rect.y + rect.h + res.dy);
  if (by) res.gy = guide(by.line, rect.x + res.dx, rect.x + rect.w + res.dx);
  return res;
}

/**
 * Snap a single edge position (used while resizing and scaling); `s0..s1` is the moving item's
 * extent along the edge. Returns the corrected position and the line it met, if any.
 */
export function snapEdge(
  pos: number,
  cands: SnapLine[],
  threshold: number,
  grid: number | null,
  s0: number,
  s1: number,
  reach: number,
): { pos: number; line: SnapLine | null } {
  const b = best([pos], cands, threshold, s0, s1, reach);
  if (b) return { pos: b.line.at, line: b.line };
  if (grid) {
    const g = Math.round(pos / grid) * grid;
    if (Math.abs(g - pos) <= threshold) return { pos: g, line: null };
  }
  return { pos, line: null };
}

/** Snap threshold in world units for the given zoom (8 screen pixels). */
export const snapThreshold = (zoom: number): number => 8 / zoom;

/**
 * How far away (in screen pixels) an item may be and still offer snap lines; 0 = anywhere in
 * view. The menu cycles through these; see toolSettings.snapReach.
 */
export const SNAP_REACHES: { px: number; label: string }[] = [
  { px: 150, label: 'Near' },
  { px: 300, label: 'Medium' },
  { px: 600, label: 'Far' },
  { px: 0, label: 'Whole view' },
];

/** Snap reach in world units for the given zoom and setting. */
export const snapReach = (zoom: number, px: number): number => (px > 0 ? px / zoom : Infinity);

export interface SnapGeometry {
  geometry: Geometry;
  gx: SnapGuide | null;
  gy: SnapGuide | null;
}

/**
 * Snap the dragged edges of a resized (unrotated) box. Free resizes snap each dragged edge on
 * its own; aspect-locked resizes snap whichever dragged edge is closest to a line and scale the
 * other dimension to match, keeping the opposite side (or centre, for side handles) fixed.
 */
export function snapResize(
  handle: Handle,
  g: Geometry,
  cands: SnapCandidates,
  threshold: number,
  grid: number | null,
  keepAspect: boolean,
  minSize: number,
  reach = Infinity,
): SnapGeometry {
  const out: SnapGeometry = { geometry: { ...g }, gx: null, gy: null };
  const r = out.geometry;
  const edgeX = handle.sx === 1 ? g.x + g.w : g.x;
  const edgeY = handle.sy === 1 ? g.y + g.h : g.y;
  const sx = handle.sx ? snapEdge(edgeX, cands.xs, threshold, grid, g.y, g.y + g.h, reach) : null;
  const sy = handle.sy ? snapEdge(edgeY, cands.ys, threshold, grid, g.x, g.x + g.w, reach) : null;
  const hitX = sx && sx.pos !== edgeX ? sx : null;
  const hitY = sy && sy.pos !== edgeY ? sy : null;

  if (!keepAspect) {
    if (hitX) {
      if (handle.sx === 1) r.w = Math.max(minSize, hitX.pos - r.x);
      else {
        const right = r.x + r.w;
        r.x = Math.min(hitX.pos, right - minSize);
        r.w = right - r.x;
      }
    }
    if (hitY) {
      if (handle.sy === 1) r.h = Math.max(minSize, hitY.pos - r.y);
      else {
        const bottom = r.y + r.h;
        r.y = Math.min(hitY.pos, bottom - minSize);
        r.h = bottom - r.y;
      }
    }
    if (hitX?.line) out.gx = guide(hitX.line, r.y, r.y + r.h);
    if (hitY?.line) out.gy = guide(hitY.line, r.x, r.x + r.w);
    return out;
  }

  // aspect locked: take the closer of the two candidate edges and scale uniformly
  const dX = hitX ? Math.abs(hitX.pos - edgeX) : Infinity;
  const dY = hitY ? Math.abs(hitY.pos - edgeY) : Infinity;
  if (dX === Infinity && dY === Infinity) return out;
  let scale: number;
  let matched: { line: SnapLine | null; vertical: boolean };
  if (dX <= dY && hitX) {
    const w = handle.sx === 1 ? hitX.pos - g.x : g.x + g.w - hitX.pos;
    scale = w / g.w;
    matched = { line: hitX.line, vertical: true };
  } else if (hitY) {
    const h = handle.sy === 1 ? hitY.pos - g.y : g.y + g.h - hitY.pos;
    scale = h / g.h;
    matched = { line: hitY.line, vertical: false };
  } else {
    return out;
  }
  const w = g.w * scale;
  const h = g.h * scale;
  if (!(w >= minSize && h >= minSize)) return { geometry: { ...g }, gx: null, gy: null };
  // anchor: the side opposite the handle, or the centre on an axis the handle does not drag
  r.x = handle.sx === 1 ? g.x : handle.sx === -1 ? g.x + g.w - w : g.x + g.w / 2 - w / 2;
  r.y = handle.sy === 1 ? g.y : handle.sy === -1 ? g.y + g.h - h : g.y + g.h / 2 - h / 2;
  r.w = w;
  r.h = h;
  if (matched.line) {
    if (matched.vertical) out.gx = guide(matched.line, r.y, r.y + r.h);
    else out.gy = guide(matched.line, r.x, r.x + r.w);
  }
  return out;
}

/**
 * Snap a uniform group scale: the dragged corner of the scaled bounding box snaps to the closest
 * line on either axis. Returns the corrected pointer to feed back into scaleGroup, or null.
 */
export function snapScalePointer(
  bbox: Rect,
  handle: Handle,
  scale: number,
  cands: SnapCandidates,
  threshold: number,
  grid: number | null,
  reach = Infinity,
): { pointer: { x: number; y: number }; gx: SnapGuide | null; gy: SnapGuide | null } | null {
  const ax = handle.sx === 1 ? bbox.x : bbox.x + bbox.w;
  const ay = handle.sy === 1 ? bbox.y : bbox.y + bbox.h;
  // the scaled box's extent on one axis, between the fixed corner and the dragged one
  const span = (a: number, dir: number, len: number, k: number): [number, number] => {
    const b = a + dir * len * k;
    return [Math.min(a, b), Math.max(a, b)];
  };
  const edgeX = ax + handle.sx * bbox.w * scale;
  const edgeY = ay + handle.sy * bbox.h * scale;
  const sx = snapEdge(edgeX, cands.xs, threshold, grid, ...span(ay, handle.sy, bbox.h, scale), reach);
  const sy = snapEdge(edgeY, cands.ys, threshold, grid, ...span(ax, handle.sx, bbox.w, scale), reach);
  const dX = sx.pos !== edgeX ? Math.abs(sx.pos - edgeX) : Infinity;
  const dY = sy.pos !== edgeY ? Math.abs(sy.pos - edgeY) : Infinity;
  if (dX === Infinity && dY === Infinity) return null;
  let s: number;
  let gx: SnapGuide | null = null;
  let gy: SnapGuide | null = null;
  if (dX <= dY) {
    s = bbox.w > 0 ? ((sx.pos - ax) * handle.sx) / bbox.w : scale;
    if (sx.line) gx = guide(sx.line, ...span(ay, handle.sy, bbox.h, s));
  } else {
    s = bbox.h > 0 ? ((sy.pos - ay) * handle.sy) / bbox.h : scale;
    if (sy.line) gy = guide(sy.line, ...span(ax, handle.sx, bbox.w, s));
  }
  if (!(s > 0)) return null;
  return { pointer: { x: ax + handle.sx * bbox.w * s, y: ay + handle.sy * bbox.h * s }, gx, gy };
}
